import { useState, useCallback, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { OnboardingStepState } from "@/lib/onboarding";

interface ReviewPublishStepProps {
  readonly siteType: string;
  readonly steps: readonly OnboardingStepState[];
  readonly tenantSlug: string;
  readonly onPublish: () => void;
}

const STEP_LABELS: Record<string, string> = {
  welcome: "\u05D1\u05E8\u05D5\u05DA \u05D4\u05D1\u05D0",
  business_info: "\u05E4\u05E8\u05D8\u05D9 \u05E2\u05E1\u05E7",
  logo_upload: "\u05DC\u05D5\u05D2\u05D5",
  payment_setup: "\u05EA\u05E9\u05DC\u05D5\u05DD",
  shipping_config: "\u05DE\u05E9\u05DC\u05D5\u05D7",
  first_product: "\u05DE\u05D5\u05E6\u05E8 \u05E8\u05D0\u05E9\u05D5\u05DF",
  services_setup: "\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD",
  work_hours: "\u05E9\u05E2\u05D5\u05EA \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA",
  contact_form: "\u05D8\u05D5\u05E4\u05E1 \u05DC\u05D9\u05D3\u05D9\u05DD",
  whatsapp_connect: "\u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4",
  review_publish: "\u05E4\u05E8\u05E1\u05D5\u05DD",
};

export function ReviewPublishStep({ siteType, steps, tenantSlug, onPublish }: ReviewPublishStepProps): ReactNode {
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);

  const siteUrl = `https://${tenantSlug}.platform.co.il`;

  const handlePublish = useCallback(async () => {
    setPublishing(true);
    // Simulate publish — in production triggers tenant site activation
    await new Promise((r) => setTimeout(r, 2000));
    setPublished(true);
    setPublishing(false);
    onPublish();
  }, [onPublish]);

  if (published) {
    return (
      <div className="flex flex-col items-center gap-6 px-4 py-12 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-600">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
            <path d="M22 4L12 14.01l-3-3" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold">{"\u05D4\u05D0\u05EA\u05E8 \u05E9\u05DC\u05DA \u05D1\u05D0\u05D5\u05D5\u05D9\u05E8!"}</h2>
        <p className="text-muted-foreground">{"\u05DB\u05DC \u05D4\u05DB\u05D1\u05D5\u05D3. \u05D4\u05D0\u05EA\u05E8 \u05E9\u05DC\u05DA \u05D7\u05D9 \u05D5\u05DE\u05D5\u05DB\u05DF."}</p>

        <div className="flex flex-col gap-3 w-full max-w-sm">
          <a
            href={siteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-[44px] items-center justify-center rounded-md border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            {"\u05E6\u05E4\u05D4 \u05D1\u05D0\u05EA\u05E8"}
          </a>
          <a
            href="/"
            className="flex min-h-[44px] items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {"\u05DC\u05E4\u05D0\u05E0\u05DC \u05D4\u05E0\u05D9\u05D4\u05D5\u05DC"}
          </a>
        </div>
      </div>
    );
  }

  const SITE_TYPE_LABELS: Record<string, string> = {
    ecommerce: "\u05D7\u05E0\u05D5\u05EA \u05D0\u05D5\u05E0\u05DC\u05D9\u05D9\u05DF",
    booking: "\u05EA\u05D5\u05E8\u05D9\u05DD",
    landing: "\u05D3\u05E3 \u05E0\u05D7\u05D9\u05EA\u05D4",
  };

  return (
    <div className="flex flex-col gap-6 px-4 py-6">
      <h2 className="text-xl font-bold">{"\u05E1\u05E7\u05D9\u05E8\u05D4 \u05D5\u05E4\u05E8\u05E1\u05D5\u05DD"}</h2>

      <Card>
        <CardContent className="flex flex-col gap-3 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{"\u05E1\u05D5\u05D2 \u05D0\u05EA\u05E8:"}</span>
            <Badge variant="secondary">{SITE_TYPE_LABELS[siteType] ?? siteType}</Badge>
          </div>

          <div className="border-t pt-3">
            <h3 className="mb-2 text-sm font-semibold">{"\u05E6\u05E2\u05D3\u05D9\u05DD \u05E9\u05D4\u05D5\u05E9\u05DC\u05DE\u05D5:"}</h3>
            <div className="flex flex-col gap-1.5">
              {steps.filter((s) => s.id !== "review_publish").map((step) => (
                <div key={step.id} className="flex items-center gap-2 text-sm">
                  {step.completed ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-green-500">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  ) : step.skipped ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-muted-foreground">
                      <path d="M13 17l5-5-5-5M6 17l5-5-5-5" />
                    </svg>
                  ) : (
                    <div className="h-4 w-4 shrink-0 rounded-full border border-muted-foreground/40" />
                  )}
                  <span className={step.skipped ? "text-muted-foreground" : ""}>
                    {STEP_LABELS[step.id] ?? step.id}
                  </span>
                  {step.skipped && (
                    <span className="text-xs text-muted-foreground">{"\u0028\u05D3\u05D5\u05DC\u05D2\u0029"}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Button
        size="lg"
        className="min-h-[44px]"
        disabled={publishing}
        onClick={handlePublish}
      >
        {publishing ? "\u05DE\u05E4\u05E8\u05E1\u05DD..." : "\u05E4\u05E8\u05E1\u05DD \u05D0\u05EA \u05D4\u05D0\u05EA\u05E8!"}
      </Button>
    </div>
  );
}
